import { useMemo } from "react";
import {
    Users,
    Briefcase,
    CalendarDays,
    Package,
    CreditCard,
    TrendingUp,
    Wallet,
} from "lucide-react";
import { getUsers } from "@/lib/storage";
import { getAllEvents, getAllServices } from "@/lib/entitiesStore";
import { useSocialVersion } from "@/lib/hooks";

const readAllBookings = () => {
    try { return JSON.parse(localStorage.getItem("neyra.bookings.all") || "[]"); }
    catch { return []; }
};

const readAllTxns = () => {
    // Pull every user's wallet.txns stored as neara.wallet.<uid>
    try {
        const users = getUsers();
        const all = [];
        for (const u of users) {
            const raw = localStorage.getItem(`neara.wallet.${u.id}`);
            if (!raw) continue;
            const wallet = JSON.parse(raw);
            for (const t of wallet.txns || []) all.push({ ...t, userId: u.id });
        }
        return all.sort((a, b) => (b.at || 0) - (a.at || 0));
    } catch { return []; }
};

const fmt = (n) => "₦" + Number(n || 0).toLocaleString();

const AdminDashboard = () => {
    // Re-render when social/bookings change
    useSocialVersion();
    const users = useMemo(() => getUsers(), []);
    const services = useMemo(() => getAllServices(), []);
    const events = useMemo(() => getAllEvents(), []);
    const bookings = useMemo(() => readAllBookings(), []);
    const txns = useMemo(() => readAllTxns(), []);
    const gmv = bookings.filter((b) => b.status === "released" || b.status === "pending")
        .reduce((acc, b) => acc + (b.amount || 0), 0);

    const stats = [
        { id: "users",    label: "Total users",        value: users.length,         icon: Users },
        { id: "services", label: "Total services",     value: services.length,      icon: Briefcase },
        { id: "events",   label: "Total events",       value: events.length,        icon: CalendarDays },
        { id: "bookings", label: "Total bookings",     value: bookings.length,      icon: Package },
        { id: "txns",     label: "Total transactions", value: txns.length,          icon: CreditCard },
        { id: "gmv",      label: "Escrow + released",  value: fmt(gmv),             icon: Wallet },
    ];

    return (
        <div data-testid="admin-dashboard" className="space-y-6">
            <div>
                <h2 className="font-display text-[22px] font-semibold tracking-tight">Overview</h2>
                <p className="text-[13px] text-muted-foreground">
                    Platform health at a glance. Data reflects live local store and syncs to Supabase when configured.
                </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="admin-dashboard-stats">
                {stats.map((s) => {
                    const Icon = s.icon;
                    return (
                        <div
                            key={s.id}
                            data-testid={`admin-stat-${s.id}`}
                            className="rounded-2xl border border-border bg-card p-4"
                        >
                            <div className="flex items-center justify-between text-muted-foreground">
                                <span className="text-[11px] uppercase tracking-wider">{s.label}</span>
                                <Icon size={14} />
                            </div>
                            <div className="mt-1.5 font-display text-[22px] font-semibold">{s.value}</div>
                        </div>
                    );
                })}
            </div>

            <section className="rounded-2xl border border-border bg-card p-4" data-testid="admin-dashboard-recent-bookings">
                <h3 className="font-display text-[14px] font-semibold mb-2 inline-flex items-center gap-2">
                    <TrendingUp size={14} className="text-primary" />
                    Recent bookings
                </h3>
                {bookings.length === 0 ? (
                    <p className="text-[12.5px] text-muted-foreground">No bookings yet.</p>
                ) : (
                    <ul className="divide-y divide-border">
                        {bookings.slice(0, 8).map((b) => (
                            <li key={b.id} className="py-2 flex items-center gap-3 text-[13px]">
                                <span className="font-medium truncate flex-1">{b.serviceName}</span>
                                <span className="text-muted-foreground truncate">{b.buyerName} → {b.sellerName}</span>
                                <span className="font-semibold">{fmt(b.amount)}</span>
                                <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground w-20 text-right">{b.status}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
};

export default AdminDashboard;
